/**
 * Updated Mock API service for local development
 * Uses the new pricing model for consistent pricing between estimates and proposals
 */
import { calculateEOQ, calculateReorderPoint, calculateSafetyStock } from './inventory-utils';
import { 
  classifyProjectComplexity, 
  calculateProjectCosts, 
  getImplementationTimeline
} from './pricing-model';
import {
  extractProjectFeatures,
  determineComplexity,
  calculateDeterministicCosts
} from './deterministic-pricing';
import { getModelParams } from './model-config';

/**
 * Process inventory data and generate optimization recommendations
 * @param {Object} data - Inventory data and parameters
 * @returns {Object} - Optimization results
 */
export const processInventoryData = async (data) => {
  // Simulate API delay with fixed timeout
  const delay = 2000; // Fixed delay for security
  await new Promise(resolve => setTimeout(resolve, delay));
  
  const { skuCount, warehouseCount, seasonalFactors, promotionalEvents } = data;
  
  // Generate mock results
  const results = {
    summary: {
      totalSkus: parseInt(skuCount) || 1000,
      warehouseCount: parseInt(warehouseCount) || 1,
      forecastAccuracy: `${Math.floor(80 + Math.random() * 15)}%`,
      potentialSavings: `$${Math.floor(300000 + Math.random() * 300000).toLocaleString()}`,
      stockoutReduction: `${Math.floor(30 + Math.random() * 30)}%`
    },
    recommendations: {
      highPriority: [],
      seasonalAdjustments: []
    }
  };
  
  // Generate high priority SKU recommendations
  for (let i = 0; i < 5; i++) {
    const sku = `SKU-${Math.floor(1000 + Math.random() * 9000)}`;
    const warehouse = `WH-${String(Math.floor(Math.random() * warehouseCount) + 1).padStart(2, '0')}`;
    const annualDemand = Math.floor(1000 + Math.random() * 5000);
    const orderCost = 50;
    const holdingCost = 10;
    const leadTimeDays = Math.floor(3 + Math.random() * 14);
    const stdDevDemand = Math.floor(5 + Math.random() * 20);
    const serviceLevel = 1.96; // ~95% service level
    
    const eoq = Math.floor(calculateEOQ(annualDemand, orderCost, holdingCost));
    const safetyStock = Math.floor(calculateSafetyStock(serviceLevel, leadTimeDays, stdDevDemand));
    const leadTimeDemand = Math.floor((annualDemand / 365) * leadTimeDays);
    const reorderPoint = Math.floor(calculateReorderPoint(leadTimeDemand, safetyStock));
    
    results.recommendations.highPriority.push({
      sku,
      warehouse,
      reorderPoint,
      eoq,
      safetyStock
    });
  }
  
  // Generate seasonal adjustments if applicable
  if (seasonalFactors) {
    const seasons = ['Spring', 'Summer', 'Fall', 'Winter', 'Holiday'];
    const skuGroups = ['Seasonal Products', 'Electronics', 'Apparel', 'Home Goods', 'Outdoor Equipment'];
    
    for (let i = 0; i < 5; i++) {
      results.recommendations.seasonalAdjustments.push({
        season: seasons[i % seasons.length],
        skuGroup: skuGroups[i % skuGroups.length],
        adjustmentFactor: (1 + (Math.random() * 0.8 + 0.2)).toFixed(2)
      });
    }
  }
  
  // Add promotional adjustments if applicable
  if (promotionalEvents) {
    results.recommendations.promotionalAdjustments = [
      { event: 'Black Friday', skuGroup: 'Electronics', adjustmentFactor: (1.8 + Math.random() * 0.5).toFixed(2) },
      { event: 'Back to School', skuGroup: 'Office Supplies', adjustmentFactor: (1.4 + Math.random() * 0.3).toFixed(2) },
      { event: 'Summer Sale', skuGroup: 'Outdoor Equipment', adjustmentFactor: (1.3 + Math.random() * 0.3).toFixed(2) }
    ];
  }
  
  return results;
};

// Function to analyze requirements and generate appropriate proposal
const generateProposalTemplate = (requirements, context = {}) => {
  // Analyze requirements to determine project type if not provided in context
  const lowerReq = requirements.toLowerCase();
  let projectType = context.projectType;
  
  if (!projectType) {
    // Check for inventory management keywords
    if (lowerReq.includes('inventory') || lowerReq.includes('stock') || lowerReq.includes('order')) {
      projectType = 'inventory';
    }
    // Check for chatbot keywords
    else if (lowerReq.includes('chatbot') || lowerReq.includes('chat') || lowerReq.includes('conversation')) {
      projectType = 'chatbot';
    }
    // Check for data analysis keywords
    else if (lowerReq.includes('data') || lowerReq.includes('analytics') || lowerReq.includes('insight')) {
      projectType = 'analytics';
    }
    // Check for document AI keywords
    else if (lowerReq.includes('document') || lowerReq.includes('contract') || 
            lowerReq.includes('pdf') || lowerReq.includes('extract')) {
      projectType = 'document_ai';
    }
    // Check for computer vision keywords
    else if (lowerReq.includes('image') || lowerReq.includes('vision') || 
            lowerReq.includes('object detection') || lowerReq.includes('recognition')) {
      projectType = 'computer_vision';
    }
    // Check for GenAI agent keywords
    else if (lowerReq.includes('agent') || lowerReq.includes('assistant') || 
            lowerReq.includes('chatgpt') || lowerReq.includes('autonomous')) {
      projectType = 'genai_agent';
    }
    // Check for recommendation system keywords
    else if (lowerReq.includes('recommend') || lowerReq.includes('suggestion')) {
      projectType = 'recommendation';
    }
    // Default to a general AI solution proposal
    else {
      projectType = 'default';
    }
  }
  
  // Create a clean project context object for pricing calculations
  // Only include relevant fields based on project type to avoid interference between project types
  const baseContext = {
    projectType,
    useSelfServiceWizard: context.useSelfServiceWizard !== undefined ? context.useSelfServiceWizard : true,
    integrationType: context.integrationType || 'none',
    dataSize: context.dataSize || { size: 100, unit: 'GB' },
    hasIoTIntegration: context.hasIoTIntegration || false,
    hasRealtimeRequirement: context.hasRealtimeRequirement || false,
    hasComplianceRequirements: context.hasComplianceRequirements || false,
    isEnterpriseScale: context.isEnterpriseScale || false,
    integrationCount: context.integrationCount || 1
  };
  
  // Add project-specific fields based on project type
  let projectContext = { ...baseContext };
  
  switch(projectType) {
    case 'inventory':
      projectContext.skuCount = context.skuCount || '';
      projectContext.productCount = context.productCount || '';
      projectContext.warehouseCount = context.warehouseCount || 1;
      break;
    case 'document_ai':
      projectContext.documentCount = context.documentCount || '';
      projectContext.specializedUseCase = context.specializedUseCase || 'none';
      break;
    case 'genai_agent':
      projectContext.needsMemory = context.needsMemory || false;
      projectContext.needsTools = context.needsTools || false;
      projectContext.needsOrchestration = context.needsOrchestration || false;
      break;
    case 'computer_vision':
    case 'recommendation':
      projectContext.specializedUseCase = context.specializedUseCase || 'none';
      if (projectType === 'recommendation') {
        projectContext.skuCount = context.skuCount || '';
        projectContext.productCount = context.productCount || '';
      }
      break;
    default:
      // No additional fields needed for other project types
      break;
  }
  
  // Calculate costs using our pricing model
  let costs, complexity, timeline;
  
  if (context.useDeterministicPricing) {
    // Use deterministic pricing for consistency
    costs = calculateDeterministicCosts(projectContext);
    complexity = costs.complexity;
    
    // Force the complexity to be consistent
    projectContext.forceComplexity = complexity;
    timeline = getImplementationTimeline(complexity, projectContext);
  } else {
    // Use the original pricing model
    costs = calculateProjectCosts(projectContext);
    complexity = context.complexity || costs.complexity;
    timeline = getImplementationTimeline(complexity, projectContext);
  }
  
  // Generate a proposal based on the project type and complexity
  switch (projectType) {
    case 'inventory':
      return generateInventorySystemProposal(requirements, projectContext, costs, timeline);
    case 'chatbot':
      return generateChatbotProposal(requirements, projectContext, costs, timeline);
    case 'analytics':
      return generateDataAnalyticsProposal(requirements, projectContext, costs, timeline);
    case 'document_ai':
      return generateDocumentAIProposal(requirements, projectContext, costs, timeline);
    case 'computer_vision':
      return generateComputerVisionProposal(requirements, projectContext, costs, timeline);
    case 'genai_agent':
      return generateGenAIAgentProposal(requirements, projectContext, costs, timeline);
    case 'recommendation':
      return generateRecommendationSystemProposal(requirements, projectContext, costs, timeline);
    default:
      return generateGeneralAIProposal(requirements, projectContext, costs, timeline);
  }
};

// Inventory Management System Proposal
const generateInventorySystemProposal = (requirements, context, costs, timeline) => {
  // Extract key information from requirements and context
  const productCount = context.skuCount || context.productCount || 500;
  const warehouseCount = typeof context.warehouseCount === 'number' ? context.warehouseCount : 1;
  const complexity = costs.complexity;
  
  // Determine appropriate solution components based on requirements
  const uiComponents = [];
  uiComponents.push('Simple web dashboard for inventory management');
  if (productCount > 20) uiComponents.push('Bulk import/export functionality');
  if (complexity === 'complex') uiComponents.push('Advanced reporting and analytics dashboard');
  
  const aiComponents = [];
  aiComponents.push('Basic demand forecasting based on historical data');
  aiComponents.push('Automated reorder point calculation');
  if (productCount > 30) aiComponents.push('Inventory optimization suggestions');
  if (complexity === 'complex') aiComponents.push('Advanced predictive analytics for inventory management');
  
  return `# Fox River AI Project Proposal

![Fox River AI Logo](https://relufox.ai/logo.png)

**Date:** ${new Date().toLocaleDateString()}
**Client:** Demo Client
**Contact:** info@relufox.ai | (630) 940-8837

## Project Overview

Based on your requirements: "${requirements}", we propose a ${complexity} complexity inventory management solution tailored for your business that leverages AI to optimize your inventory levels and automate ordering processes.

## Solution Architecture

We recommend building a ${productCount > 500 ? 'comprehensive' : 'lightweight'} inventory management system using our Self Service Build Wizard to accelerate development with the following components:

1. **User Interface**
   ${uiComponents.map(item => `- ${item}`).join('\n   ')}

2. **Core Functionality**
   - Inventory tracking for ${productCount.toLocaleString()} ${productCount > 500 ? 'SKUs' : 'products'}
   - Purchase order generation based on inventory levels
   - Historical sales data analysis
   ${warehouseCount > 1 ? `- Support for ${warehouseCount} warehouse locations` : '- Single warehouse inventory management'}
   ${context.integrationType ? `- Integration with your existing ${context.integrationType === 'general' ? 'systems' : context.integrationType + ' system'}` : ''}

3. **AI-Powered Features**
   ${aiComponents.map(item => `- ${item}`).join('\n   ')}
   ${complexity === 'complex' ? '- Machine learning for seasonal demand prediction' : ''}

4. **Data Management**
   - ${complexity === 'basic' ? 'Simple database for inventory records' : complexity === 'complex' ? 'Scalable database architecture for large inventory catalogs' : 'Optimized database structure for efficient inventory management'}
   - Secure cloud backup of all inventory data
   - ${complexity === 'complex' ? 'Comprehensive reporting and analytics dashboard' : 'Export functionality for reporting'}
   ${complexity === 'complex' ? '- Data warehousing for historical analysis' : ''}

## Implementation Timeline

| Phase | Duration | Description |
|-------|----------|-------------|
| Discovery | ${timeline.discovery} week${timeline.discovery > 1 ? 's' : ''} | Requirements gathering and process mapping |
| Design | ${timeline.design} week${timeline.design > 1 ? 's' : ''} | Solution design and data structure planning |
| Development | ${timeline.development} week${timeline.development > 1 ? 's' : ''} | Implementation of core features and AI components |
| Testing | ${timeline.testing} week${timeline.testing > 1 ? 's' : ''} | Quality assurance and user acceptance testing |
| Deployment | ${timeline.deployment} week${timeline.deployment > 1 ? 's' : ''} | Setup, data import, and training |

## Cost Estimate

Based on your specific requirements, we estimate the following costs:

### Development Costs
- Discovery & Design: $${costs.breakdown.development.discoveryDesign.min.toLocaleString()} - $${costs.breakdown.development.discoveryDesign.max.toLocaleString()}
- Implementation: $${costs.breakdown.development.implementation.min.toLocaleString()} - $${costs.breakdown.development.implementation.max.toLocaleString()}
- Testing & Deployment: $${costs.breakdown.development.testingDeployment.min.toLocaleString()} - $${costs.breakdown.development.testingDeployment.max.toLocaleString()}
- **Total Development:** $${costs.totalDevRange.min.toLocaleString()} - $${costs.totalDevRange.max.toLocaleString()}

### Ongoing Costs (Monthly)
- AWS Infrastructure: $${costs.breakdown.monthly.infrastructure.min.toLocaleString()} - $${costs.breakdown.monthly.infrastructure.max.toLocaleString()}
- Maintenance & Support: $${costs.breakdown.monthly.maintenance.min.toLocaleString()} - $${costs.breakdown.monthly.maintenance.max.toLocaleString()}
- Model Updates & Training: $${costs.breakdown.monthly.modelUpdates.min.toLocaleString()} - $${costs.breakdown.monthly.modelUpdates.max.toLocaleString()}
- **Total Monthly:** $${costs.totalMonthlyRange.min.toLocaleString()} - $${costs.totalMonthlyRange.max.toLocaleString()}

## Self Service Build Wizard

Our Self Service Build Wizard significantly reduces development costs and accelerates project delivery by:
- Automating infrastructure setup and configuration
- Providing pre-built components for common AI functionalities
- Streamlining integration with AWS services
- Reducing manual coding requirements
- Enabling rapid prototyping and iteration

## Assumptions

This proposal is based on the following assumptions:
- Inventory tracking for ${productCount.toLocaleString()} products
- ${warehouseCount === 1 ? 'Single warehouse location' : `${warehouseCount} warehouse locations`}
- ${context.integrationType ? `Integration with your existing ${context.integrationType === 'general' ? 'systems' : context.integrationType + ' system'}` : 'No integration with external systems required'}
- Standard security for business applications
- Use of our Self Service Build Wizard for accelerated development

## Next Steps

To proceed with this project, we recommend:
1. Schedule a brief discovery call to confirm your requirements
2. Review your current inventory process and historical data
3. Finalize the project scope and timeline
4. Begin implementation

Please contact us at info@relufox.ai to discuss this proposal further.`;
};

// Chatbot Proposal
const generateChatbotProposal = (requirements, context, costs, timeline) => {
  const complexity = costs.complexity;
  
  return `# Fox River AI Project Proposal

![Fox River AI Logo](https://relufox.ai/logo.png)

**Date:** ${new Date().toLocaleDateString()}
**Client:** Demo Client
**Contact:** info@relufox.ai | (630) 940-8837

## Project Overview

Based on your requirements: "${requirements}", we propose a ${complexity} complexity conversational AI solution that will provide an engaging and helpful experience for your users.

## Solution Architecture

We recommend building a chatbot using AWS Bedrock and our Self Service Build Wizard to accelerate development with the following components:

1. **User Interface Layer**
   - Web interface with responsive design
   - Mobile compatibility
   ${complexity === 'complex' ? '- Omnichannel support (website, mobile app, social media)' : ''}

2. **Application Layer**
   - AWS Lambda for serverless processing
   - API Gateway for secure endpoints
   ${complexity === 'complex' ? '- Amazon Connect for voice integration' : ''}

3. **AI/ML Layer**
   - AWS Bedrock for natural language processing
   ${complexity !== 'basic' ? '- Amazon Comprehend for sentiment analysis' : ''}
   - Custom training for domain-specific knowledge
   ${complexity === 'complex' ? '- Advanced context management and conversation memory' : ''}

4. **Data Layer**
   - Amazon DynamoDB for customer data
   - Amazon S3 for conversation logs
   ${complexity === 'complex' ? '- Amazon RDS for structured data' : ''}

## Implementation Timeline

| Phase | Duration | Description |
|-------|----------|-------------|
| Discovery | ${timeline.discovery} week${timeline.discovery > 1 ? 's' : ''} | Requirements gathering and analysis |
| Design | ${timeline.design} week${timeline.design > 1 ? 's' : ''} | Solution architecture and conversation flow design |
| Development | ${timeline.development} week${timeline.development > 1 ? 's' : ''} | Implementation of all components |
| Testing | ${timeline.testing} week${timeline.testing > 1 ? 's' : ''} | Quality assurance and user acceptance testing |
| Deployment | ${timeline.deployment} week${timeline.deployment > 1 ? 's' : ''} | Production deployment and monitoring setup |

## Cost Estimate

Based on your specific requirements, we estimate the following costs:

### Development Costs
- Discovery & Design: $${costs.breakdown.development.discoveryDesign.min.toLocaleString()} - $${costs.breakdown.development.discoveryDesign.max.toLocaleString()}
- Implementation: $${costs.breakdown.development.implementation.min.toLocaleString()} - $${costs.breakdown.development.implementation.max.toLocaleString()}
- Testing & Deployment: $${costs.breakdown.development.testingDeployment.min.toLocaleString()} - $${costs.breakdown.development.testingDeployment.max.toLocaleString()}
- **Total Development:** $${costs.totalDevRange.min.toLocaleString()} - $${costs.totalDevRange.max.toLocaleString()}

### Ongoing Costs (Monthly)
- AWS Infrastructure: $${costs.breakdown.monthly.infrastructure.min.toLocaleString()} - $${costs.breakdown.monthly.infrastructure.max.toLocaleString()}
- Maintenance & Support: $${costs.breakdown.monthly.maintenance.min.toLocaleString()} - $${costs.breakdown.monthly.maintenance.max.toLocaleString()}
- Model Updates & Training: $${costs.breakdown.monthly.modelUpdates.min.toLocaleString()} - $${costs.breakdown.monthly.modelUpdates.max.toLocaleString()}
- **Total Monthly:** $${costs.totalMonthlyRange.min.toLocaleString()} - $${costs.totalMonthlyRange.max.toLocaleString()}

## Self Service Build Wizard

Our Self Service Build Wizard significantly reduces development costs and accelerates project delivery by:
- Automating chatbot infrastructure setup and configuration
- Providing pre-built conversation flows and intent recognition
- Streamlining integration with AWS Bedrock and other AI services
- Reducing manual coding requirements by up to 60%
- Enabling rapid prototyping and iteration of conversation designs

## Assumptions

This proposal is based on the following assumptions:
- The chatbot will handle approximately ${complexity === 'basic' ? '500' : complexity === 'standard' ? '1,000' : '5,000'} customer inquiries per day
- ${context.integrationType ? `Integration with existing ${context.integrationType === 'general' ? 'customer service systems' : context.integrationType + ' system'} via API` : 'No integration with external systems required'}
- No more than ${complexity === 'basic' ? '3' : complexity === 'standard' ? '5' : '10'} custom intents beyond standard customer inquiries
- Standard security requirements (no special compliance needs)
- Use of our Self Service Build Wizard for accelerated development

## Next Steps

To proceed with this project, we recommend:
1. Schedule a detailed discovery workshop
2. Finalize requirements and scope
3. Sign statement of work
4. Begin design phase

Please contact us at info@relufox.ai to discuss this proposal further.`;
};

// Data Analytics Proposal
const generateDataAnalyticsProposal = (requirements, context, costs, timeline) => {
  const complexity = costs.complexity;
  // eslint-disable-next-line no-unused-vars
  const integrationType = context.integrationType;
  const dataSize = context.dataSize || { size: 500, unit: 'GB' };
  
  return `# Fox River AI Project Proposal

![Fox River AI Logo](https://relufox.ai/logo.png)

**Date:** ${new Date().toLocaleDateString()}
**Client:** Demo Client
**Contact:** info@relufox.ai | (630) 940-8837

## Project Overview

Based on your requirements: "${requirements}", we propose a ${complexity} complexity data analytics solution powered by AI to transform your raw data into actionable business insights.

## Solution Architecture

We recommend building a data analytics platform using our Self Service Build Wizard to accelerate development with the following components:

1. **Data Collection Layer**
   - Data connectors for various sources
   - ETL pipelines using AWS Glue
   ${complexity === 'complex' ? '- Real-time data streaming with Amazon Kinesis' : ''}

2. **Processing Layer**
   - ${complexity === 'basic' ? 'AWS Lambda for serverless processing' : 'Amazon EMR for big data processing'}
   - AWS Lambda for serverless processing
   - Amazon SageMaker for machine learning

3. **Analytics Layer**
   - ${complexity === 'basic' ? 'Basic' : complexity === 'standard' ? 'Custom' : 'Advanced'} ML models for predictive analytics
   - Amazon QuickSight for visualization
   ${complexity === 'complex' ? '- Natural language query capabilities' : ''}

4. **Storage Layer**
   - Amazon S3 for data lake
   ${complexity !== 'basic' ? '- Amazon Redshift for data warehouse' : ''}
   - Amazon DynamoDB for operational data

## Implementation Timeline

| Phase | Duration | Description |
|-------|----------|-------------|
| Discovery | ${timeline.discovery} week${timeline.discovery > 1 ? 's' : ''} | Data assessment, requirements gathering, and use case definition |
| Design | ${timeline.design} week${timeline.design > 1 ? 's' : ''} | Data architecture, pipeline design, and analytics framework |
| Development | ${timeline.development} week${timeline.development > 1 ? 's' : ''} | Implementation of data pipelines, models, and dashboards |
| Testing | ${timeline.testing} week${timeline.testing > 1 ? 's' : ''} | Quality assurance, model validation, and performance testing |
| Deployment | ${timeline.deployment} week${timeline.deployment > 1 ? 's' : ''} | Production deployment and knowledge transfer |

## Cost Estimate

Based on your specific requirements, we estimate the following costs:

### Development Costs
- Discovery & Design: $${costs.breakdown.development.discoveryDesign.min.toLocaleString()} - $${costs.breakdown.development.discoveryDesign.max.toLocaleString()}
- Implementation: $${costs.breakdown.development.implementation.min.toLocaleString()} - $${costs.breakdown.development.implementation.max.toLocaleString()}
- Testing & Deployment: $${costs.breakdown.development.testingDeployment.min.toLocaleString()} - $${costs.breakdown.development.testingDeployment.max.toLocaleString()}
- **Total Development:** $${costs.totalDevRange.min.toLocaleString()} - $${costs.totalDevRange.max.toLocaleString()}

### Ongoing Costs (Monthly)
- AWS Infrastructure: $${costs.breakdown.monthly.infrastructure.min.toLocaleString()} - $${costs.breakdown.monthly.infrastructure.max.toLocaleString()}
- Maintenance & Support: $${costs.breakdown.monthly.maintenance.min.toLocaleString()} - $${costs.breakdown.monthly.maintenance.max.toLocaleString()}
- Model Updates & Training: $${costs.breakdown.monthly.modelUpdates.min.toLocaleString()} - $${costs.breakdown.monthly.modelUpdates.max.toLocaleString()}
- **Total Monthly:** $${costs.totalMonthlyRange.min.toLocaleString()} - $${costs.totalMonthlyRange.max.toLocaleString()}

## Self Service Build Wizard

Our Self Service Build Wizard significantly reduces development costs and accelerates project delivery by:
- Automating data pipeline setup and configuration
- Providing pre-built analytics components and visualization templates
- Streamlining integration with AWS analytics services
- Reducing manual coding requirements by up to 50%
- Enabling rapid prototyping and iteration of analytics models

## Assumptions

This proposal is based on the following assumptions:
- Data volume of up to ${dataSize.size} ${dataSize.unit} initially
- Integration with up to ${complexity === 'basic' ? '2' : complexity === 'standard' ? '5' : '10'} data sources
- Up to ${complexity === 'basic' ? '5' : complexity === 'standard' ? '10' : '20'} custom dashboards and reports
- Standard security and compliance requirements
- Use of our Self Service Build Wizard for accelerated development

## Next Steps

To proceed with this project, we recommend:
1. Schedule a data discovery workshop
2. Conduct a proof of concept with a subset of your data
3. Finalize requirements and analytics use cases
4. Sign statement of work and begin implementation

Please contact us at info@relufox.ai to discuss this proposal further.`;
};

// Document AI Proposal
const generateDocumentAIProposal = (requirements, context, costs, timeline) => {
  const complexity = costs.complexity;
  const documentCount = context.documentCount || 1000;
  const specializedUseCase = context.specializedUseCase;
  
  // Determine the specialized use case description
  let useCaseDescription = "document processing";
  if (specializedUseCase === 'legal_nlp') {
    useCaseDescription = "legal document processing with NLP";
  } else if (specializedUseCase === 'medical_nlp') {
    useCaseDescription = "medical document analysis";
  } else if (specializedUseCase === 'financial_nlp') {
    useCaseDescription = "financial document processing";
  }
  
  return `# Fox River AI Project Proposal

![Fox River AI Logo](https://relufox.ai/logo.png)

**Date:** ${new Date().toLocaleDateString()}
**Client:** Demo Client
**Contact:** info@relufox.ai | (630) 940-8837

## Project Overview

Based on your requirements: "${requirements}", we propose a ${complexity} complexity document AI solution specialized for ${useCaseDescription} that will transform how you extract, analyze, and utilize information from your documents.

## Solution Architecture

We recommend building a document processing system using our Self Service Build Wizard to accelerate development with the following components:

1. **Document Processing Layer**
   - Document ingestion and preprocessing pipeline
   - OCR and text extraction capabilities
   ${complexity === 'complex' ? '- Advanced document classification' : '- Basic document classification'}

2. **AI/ML Layer**
   - Natural Language Processing for entity extraction
   ${complexity !== 'basic' ? '- Retrieval Augmented Generation (RAG) pipeline' : ''}
   - AWS Bedrock for document understanding
   ${complexity === 'complex' ? '- Custom trained models for specialized document types' : ''}

3. **Application Layer**
   - Document management interface
   - Search and retrieval functionality
   ${complexity === 'complex' ? '- Advanced query capabilities using natural language' : ''}

4. **Storage Layer**
   - Amazon S3 for document storage
   - Vector database for semantic search
   ${complexity === 'complex' ? '- Knowledge graph for complex relationships' : ''}

## Implementation Timeline

| Phase | Duration | Description |
|-------|----------|-------------|
| Discovery | ${timeline.discovery} week${timeline.discovery > 1 ? 's' : ''} | Document assessment, requirements gathering, and use case definition |
| Design | ${timeline.design} week${timeline.design > 1 ? 's' : ''} | Solution architecture, pipeline design, and extraction framework |
| Development | ${timeline.development} week${timeline.development > 1 ? 's' : ''} | Implementation of processing pipelines, models, and interfaces |
| Testing | ${timeline.testing} week${timeline.testing > 1 ? 's' : ''} | Quality assurance, model validation, and performance testing |
| Deployment | ${timeline.deployment} week${timeline.deployment > 1 ? 's' : ''} | Production deployment and knowledge transfer |

## Cost Estimate

Based on your specific requirements, we estimate the following costs:

### Development Costs
- Discovery & Design: $${costs.breakdown.development.discoveryDesign.min.toLocaleString()} - $${costs.breakdown.development.discoveryDesign.max.toLocaleString()}
- Implementation: $${costs.breakdown.development.implementation.min.toLocaleString()} - $${costs.breakdown.development.implementation.max.toLocaleString()}
- Testing & Deployment: $${costs.breakdown.development.testingDeployment.min.toLocaleString()} - $${costs.breakdown.development.testingDeployment.max.toLocaleString()}
- **Total Development:** $${costs.totalDevRange.min.toLocaleString()} - $${costs.totalDevRange.max.toLocaleString()}

### Ongoing Costs (Monthly)
- AWS Infrastructure: $${costs.breakdown.monthly.infrastructure.min.toLocaleString()} - $${costs.breakdown.monthly.infrastructure.max.toLocaleString()}
- Maintenance & Support: $${costs.breakdown.monthly.maintenance.min.toLocaleString()} - $${costs.breakdown.monthly.maintenance.max.toLocaleString()}
- Model Updates & Training: $${costs.breakdown.monthly.modelUpdates.min.toLocaleString()} - $${costs.breakdown.monthly.modelUpdates.max.toLocaleString()}
- **Total Monthly:** $${costs.totalMonthlyRange.min.toLocaleString()} - $${costs.totalMonthlyRange.max.toLocaleString()}

## Self Service Build Wizard

Our Self Service Build Wizard significantly reduces development costs and accelerates project delivery by:
- Automating document processing pipeline setup
- Providing pre-built extraction components for common document types
- Streamlining integration with AWS AI services
- Reducing manual coding requirements by up to 50%
- Enabling rapid prototyping and iteration of extraction models

## Assumptions

This proposal is based on the following assumptions:
- Processing approximately ${documentCount.toLocaleString()} documents
- ${specializedUseCase ? `Specialized processing for ${useCaseDescription}` : 'Standard document processing requirements'}
- Up to ${complexity === 'basic' ? '5' : complexity === 'standard' ? '10' : '20'} different document types
- Standard security and compliance requirements${specializedUseCase === 'medical_nlp' ? ' with additional HIPAA compliance' : ''}
- Use of our Self Service Build Wizard for accelerated development

## Next Steps

To proceed with this project, we recommend:
1. Schedule a document assessment workshop
2. Conduct a proof of concept with sample documents
3. Finalize requirements and extraction use cases
4. Sign statement of work and begin implementation

Please contact us at info@relufox.ai to discuss this proposal further.`;
};

// Computer Vision Proposal
const generateComputerVisionProposal = (requirements, context, costs, timeline) => {
  const complexity = costs.complexity;
  const specializedUseCase = context.specializedUseCase;
  
  // Determine the specialized use case description
  let useCaseDescription = "image processing and object detection";
  if (specializedUseCase === 'medical_imaging') {
    useCaseDescription = "medical imaging analysis";
  }
  
  return `# Fox River AI Project Proposal

![Fox River AI Logo](https://relufox.ai/logo.png)

**Date:** ${new Date().toLocaleDateString()}
**Client:** Demo Client
**Contact:** info@relufox.ai | (630) 940-8837

## Project Overview

Based on your requirements: "${requirements}", we propose a ${complexity} complexity computer vision solution specialized for ${useCaseDescription} that will transform how you analyze and extract insights from visual data.

## Solution Architecture

We recommend building a computer vision system using our Self Service Build Wizard to accelerate development with the following components:

1. **Image Processing Layer**
   - Image ingestion and preprocessing pipeline
   - Image enhancement and normalization
   ${complexity === 'complex' ? '- Advanced image segmentation' : '- Basic image segmentation'}

2. **AI/ML Layer**
   - Object detection and classification models
   ${complexity !== 'basic' ? '- Instance segmentation capabilities' : ''}
   - AWS SageMaker for model training and deployment
   ${complexity === 'complex' ? '- Custom trained models for specialized image types' : ''}

3. **Application Layer**
   - Image management interface
   - Analysis and reporting functionality
   ${complexity === 'complex' ? '- Real-time processing capabilities' : ''}

4. **Storage Layer**
   - Amazon S3 for image storage
   - Metadata database for search and retrieval
   ${complexity === 'complex' ? '- Edge computing integration for distributed processing' : ''}

## Implementation Timeline

| Phase | Duration | Description |
|-------|----------|-------------|
| Discovery | ${timeline.discovery} week${timeline.discovery > 1 ? 's' : ''} | Image assessment, requirements gathering, and use case definition |
| Design | ${timeline.design} week${timeline.design > 1 ? 's' : ''} | Solution architecture, pipeline design, and model selection |
| Development | ${timeline.development} week${timeline.development > 1 ? 's' : ''} | Implementation of processing pipelines, models, and interfaces |
| Testing | ${timeline.testing} week${timeline.testing > 1 ? 's' : ''} | Quality assurance, model validation, and performance testing |
| Deployment | ${timeline.deployment} week${timeline.deployment > 1 ? 's' : ''} | Production deployment and knowledge transfer |

## Cost Estimate

Based on your specific requirements, we estimate the following costs:

### Development Costs
- Discovery & Design: $${costs.breakdown.development.discoveryDesign.min.toLocaleString()} - $${costs.breakdown.development.discoveryDesign.max.toLocaleString()}
- Implementation: $${costs.breakdown.development.implementation.min.toLocaleString()} - $${costs.breakdown.development.implementation.max.toLocaleString()}
- Testing & Deployment: $${costs.breakdown.development.testingDeployment.min.toLocaleString()} - $${costs.breakdown.development.testingDeployment.max.toLocaleString()}
- **Total Development:** $${costs.totalDevRange.min.toLocaleString()} - $${costs.totalDevRange.max.toLocaleString()}

### Ongoing Costs (Monthly)
- AWS Infrastructure: $${costs.breakdown.monthly.infrastructure.min.toLocaleString()} - $${costs.breakdown.monthly.infrastructure.max.toLocaleString()}
- Maintenance & Support: $${costs.breakdown.monthly.maintenance.min.toLocaleString()} - $${costs.breakdown.monthly.maintenance.max.toLocaleString()}
- Model Updates & Training: $${costs.breakdown.monthly.modelUpdates.min.toLocaleString()} - $${costs.breakdown.monthly.modelUpdates.max.toLocaleString()}
- **Total Monthly:** $${costs.totalMonthlyRange.min.toLocaleString()} - $${costs.totalMonthlyRange.max.toLocaleString()}

## Self Service Build Wizard

Our Self Service Build Wizard significantly reduces development costs and accelerates project delivery by:
- Automating computer vision pipeline setup
- Providing pre-built detection components for common object types
- Streamlining integration with AWS AI services
- Reducing manual coding requirements by up to 50%
- Enabling rapid prototyping and iteration of vision models

## Assumptions

This proposal is based on the following assumptions:
- Processing approximately ${complexity === 'basic' ? '1,000' : complexity === 'standard' ? '10,000' : '100,000'} images per month
- ${specializedUseCase ? `Specialized processing for ${useCaseDescription}` : 'Standard image processing requirements'}
- Up to ${complexity === 'basic' ? '3' : complexity === 'standard' ? '5' : '10'} different detection classes
- Standard security and compliance requirements${specializedUseCase === 'medical_imaging' ? ' with additional HIPAA compliance' : ''}
- Use of our Self Service Build Wizard for accelerated development

## Next Steps

To proceed with this project, we recommend:
1. Schedule an image assessment workshop
2. Conduct a proof of concept with sample images
3. Finalize requirements and detection use cases
4. Sign statement of work and begin implementation

Please contact us at info@relufox.ai to discuss this proposal further.`;
};

// GenAI Agent Proposal
const generateGenAIAgentProposal = (requirements, context, costs, timeline) => {
  const complexity = costs.complexity;
  const needsMemory = context.needsMemory;
  const needsTools = context.needsTools;
  const needsOrchestration = context.needsOrchestration;
  
  // Build feature description
  let features = [];
  if (needsMemory) features.push("persistent memory");
  if (needsTools) features.push("tool integration");
  if (needsOrchestration) features.push("workflow orchestration");
  
  const featuresText = features.length > 0 ? 
                     ` with ${features.join(", ")}` : "";
  
  return `# Fox River AI Project Proposal

![Fox River AI Logo](https://relufox.ai/logo.png)

**Date:** ${new Date().toLocaleDateString()}
**Client:** Demo Client
**Contact:** info@relufox.ai | (630) 940-8837

## Project Overview

Based on your requirements: "${requirements}", we propose a ${complexity} complexity GenAI agent solution${featuresText} that will provide an intelligent, autonomous assistant for your specific needs.

## Solution Architecture

We recommend building a GenAI agent using our Self Service Build Wizard to accelerate development with the following components:

1. **Agent Framework Layer**
   - AWS Bedrock for foundation model capabilities
   - ${needsMemory ? 'Advanced memory management system' : 'Basic conversation context management'}
   ${needsOrchestration ? '- LangGraph for complex workflow orchestration' : ''}

2. **Tool Integration Layer**
   ${needsTools ? '- Custom tool development for specific actions' : '- Basic tool integration capabilities'}
   ${needsTools ? '- API integration framework for external systems' : ''}
   ${complexity === 'complex' ? '- Advanced reasoning and planning capabilities' : ''}

3. **Application Layer**
   - Web interface for agent interaction
   - Admin dashboard for monitoring and configuration
   ${complexity === 'complex' ? '- Analytics dashboard for performance tracking' : ''}

4. **Data Layer**
   - Knowledge base integration
   - ${complexity !== 'basic' ? 'Vector database for semantic search' : 'Basic document retrieval'}
   ${complexity === 'complex' ? '- Multi-modal data processing capabilities' : ''}

## Implementation Timeline

| Phase | Duration | Description |
|-------|----------|-------------|
| Discovery | ${timeline.discovery} week${timeline.discovery > 1 ? 's' : ''} | Requirements gathering, use case definition, and knowledge base assessment |
| Design | ${timeline.design} week${timeline.design > 1 ? 's' : ''} | Agent architecture, tool design, and workflow planning |
| Development | ${timeline.development} week${timeline.development > 1 ? 's' : ''} | Implementation of agent capabilities, tools, and interfaces |
| Testing | ${timeline.testing} week${timeline.testing > 1 ? 's' : ''} | Quality assurance, behavior validation, and performance testing |
| Deployment | ${timeline.deployment} week${timeline.deployment > 1 ? 's' : ''} | Production deployment and knowledge transfer |

## Cost Estimate

Based on your specific requirements, we estimate the following costs:

### Development Costs
- Discovery & Design: $${costs.breakdown.development.discoveryDesign.min.toLocaleString()} - $${costs.breakdown.development.discoveryDesign.max.toLocaleString()}
- Implementation: $${costs.breakdown.development.implementation.min.toLocaleString()} - $${costs.breakdown.development.implementation.max.toLocaleString()}
- Testing & Deployment: $${costs.breakdown.development.testingDeployment.min.toLocaleString()} - $${costs.breakdown.development.testingDeployment.max.toLocaleString()}
- **Total Development:** $${costs.totalDevRange.min.toLocaleString()} - $${costs.totalDevRange.max.toLocaleString()}

### Ongoing Costs (Monthly)
- AWS Infrastructure: $${costs.breakdown.monthly.infrastructure.min.toLocaleString()} - $${costs.breakdown.monthly.infrastructure.max.toLocaleString()}
- Maintenance & Support: $${costs.breakdown.monthly.maintenance.min.toLocaleString()} - $${costs.breakdown.monthly.maintenance.max.toLocaleString()}
- Model Updates & Training: $${costs.breakdown.monthly.modelUpdates.min.toLocaleString()} - $${costs.breakdown.monthly.modelUpdates.max.toLocaleString()}
- **Total Monthly:** $${costs.totalMonthlyRange.min.toLocaleString()} - $${costs.totalMonthlyRange.max.toLocaleString()}

## Self Service Build Wizard

Our Self Service Build Wizard significantly reduces development costs and accelerates project delivery by:
- Automating agent infrastructure setup and configuration
- Providing pre-built components for memory, tools, and orchestration
- Streamlining integration with AWS AI services
- Reducing manual coding requirements by up to 60%
- Enabling rapid prototyping and iteration of agent capabilities

## Assumptions

This proposal is based on the following assumptions:
- ${needsMemory ? 'Persistent memory capabilities for conversation history' : 'Basic conversation context management'}
- ${needsTools ? `Tool integration for ${complexity === 'basic' ? '2-3' : complexity === 'standard' ? '4-6' : '7+'} different actions` : 'No complex tool integrations required'}
- ${needsOrchestration ? 'Complex workflow orchestration capabilities' : 'Simple sequential processing workflows'}
- Standard security and compliance requirements
- Use of our Self Service Build Wizard for accelerated development

## Next Steps

To proceed with this project, we recommend:
1. Schedule a detailed discovery workshop
2. Define agent capabilities and success criteria
3. Finalize requirements and scope
4. Sign statement of work and begin implementation

Please contact us at info@relufox.ai to discuss this proposal further.`;
};

// Recommendation System Proposal
const generateRecommendationSystemProposal = (requirements, context, costs, timeline) => {
  const complexity = costs.complexity;
  const productCount = context.skuCount || context.productCount || 1000;
  
  return `# Fox River AI Project Proposal

![Fox River AI Logo](https://relufox.ai/logo.png)

**Date:** ${new Date().toLocaleDateString()}
**Client:** Demo Client
**Contact:** info@relufox.ai | (630) 940-8837

## Project Overview

Based on your requirements: "${requirements}", we propose a ${complexity} complexity recommendation system that will provide personalized suggestions to enhance user experience and drive engagement.

## Solution Architecture

We recommend building a recommendation system using our Self Service Build Wizard to accelerate development with the following components:

1. **Data Processing Layer**
   - Data ingestion and preprocessing pipeline
   - User behavior tracking and analysis
   ${complexity === 'complex' ? '- Real-time event processing' : ''}

2. **AI/ML Layer**
   - ${complexity === 'basic' ? 'Content-based filtering algorithms' : complexity === 'standard' ? 'Hybrid recommendation algorithms' : 'Advanced collaborative filtering and deep learning models'}
   - AWS SageMaker for model training and deployment
   ${complexity === 'complex' ? '- A/B testing framework for algorithm optimization' : ''}

3. **Application Layer**
   - API for recommendation delivery
   - Admin dashboard for system configuration
   ${complexity === 'complex' ? '- Analytics dashboard for recommendation performance' : ''}

4. **Integration Layer**
   - ${context.integrationType ? `Integration with your ${context.integrationType === 'general' ? 'existing systems' : context.integrationType + ' system'}` : 'Standard API endpoints for external systems'}
   - Batch and real-time recommendation capabilities
   ${complexity === 'complex' ? '- Multi-channel recommendation delivery' : ''}

## Implementation Timeline

| Phase | Duration | Description |
|-------|----------|-------------|
| Discovery | ${timeline.discovery} week${timeline.discovery > 1 ? 's' : ''} | Data assessment, requirements gathering, and use case definition |
| Design | ${timeline.design} week${timeline.design > 1 ? 's' : ''} | Solution architecture, algorithm selection, and integration design |
| Development | ${timeline.development} week${timeline.development > 1 ? 's' : ''} | Implementation of data pipelines, models, and interfaces |
| Testing | ${timeline.testing} week${timeline.testing > 1 ? 's' : ''} | Quality assurance, model validation, and performance testing |
| Deployment | ${timeline.deployment} week${timeline.deployment > 1 ? 's' : ''} | Production deployment and knowledge transfer |

## Cost Estimate

Based on your specific requirements, we estimate the following costs:

### Development Costs
- Discovery & Design: $${costs.breakdown.development.discoveryDesign.min.toLocaleString()} - $${costs.breakdown.development.discoveryDesign.max.toLocaleString()}
- Implementation: $${costs.breakdown.development.implementation.min.toLocaleString()} - $${costs.breakdown.development.implementation.max.toLocaleString()}
- Testing & Deployment: $${costs.breakdown.development.testingDeployment.min.toLocaleString()} - $${costs.breakdown.development.testingDeployment.max.toLocaleString()}
- **Total Development:** $${costs.totalDevRange.min.toLocaleString()} - $${costs.totalDevRange.max.toLocaleString()}

### Ongoing Costs (Monthly)
- AWS Infrastructure: $${costs.breakdown.monthly.infrastructure.min.toLocaleString()} - $${costs.breakdown.monthly.infrastructure.max.toLocaleString()}
- Maintenance & Support: $${costs.breakdown.monthly.maintenance.min.toLocaleString()} - $${costs.breakdown.monthly.maintenance.max.toLocaleString()}
- Model Updates & Training: $${costs.breakdown.monthly.modelUpdates.min.toLocaleString()} - $${costs.breakdown.monthly.modelUpdates.max.toLocaleString()}
- **Total Monthly:** $${costs.totalMonthlyRange.min.toLocaleString()} - $${costs.totalMonthlyRange.max.toLocaleString()}

## Self Service Build Wizard

Our Self Service Build Wizard significantly reduces development costs and accelerates project delivery by:
- Automating recommendation system infrastructure setup
- Providing pre-built components for common recommendation algorithms
- Streamlining integration with AWS AI services
- Reducing manual coding requirements by up to 50%
- Enabling rapid prototyping and iteration of recommendation models

## Assumptions

This proposal is based on the following assumptions:
- Catalog size of approximately ${productCount.toLocaleString()} products
- ${context.integrationType ? `Integration with your ${context.integrationType === 'general' ? 'existing systems' : context.integrationType + ' system'}` : 'Standard API integration requirements'}
- User base of approximately ${complexity === 'basic' ? '1,000' : complexity === 'standard' ? '10,000' : '100,000'} users
- Standard security and compliance requirements
- Use of our Self Service Build Wizard for accelerated development

## Next Steps

To proceed with this project, we recommend:
1. Schedule a data assessment workshop
2. Conduct a proof of concept with sample data
3. Finalize requirements and recommendation use cases
4. Sign statement of work and begin implementation

Please contact us at info@relufox.ai to discuss this proposal further.`;
};

// General AI Solution Proposal
const generateGeneralAIProposal = (requirements, context, costs, timeline) => {
  const complexity = costs.complexity;
  
  return `# Fox River AI Project Proposal

![Fox River AI Logo](https://relufox.ai/logo.png)

**Date:** ${new Date().toLocaleDateString()}
**Client:** Demo Client
**Contact:** info@relufox.ai | (630) 940-8837

## Project Overview

Based on your requirements: "${requirements}", we propose a ${complexity} complexity AI solution to address your specific business needs.

## Solution Architecture

We recommend building a flexible AI platform using our Self Service Build Wizard to accelerate development with the following components:

1. **User Interface Layer**
   - Web interface with responsive design
   - Mobile compatibility
   - API access for system integration

2. **Application Layer**
   - AWS Lambda for serverless processing
   - API Gateway for secure endpoints
   ${complexity !== 'basic' ? '- Step Functions for complex workflows' : ''}

3. **AI/ML Layer**
   - Amazon SageMaker for custom model development
   - AWS Bedrock for foundation models
   ${complexity !== 'basic' ? '- Amazon Comprehend for text analysis' : ''}

4. **Data Layer**
   - Amazon DynamoDB for operational data
   - Amazon S3 for unstructured data storage
   ${complexity === 'complex' ? '- Amazon RDS for structured data' : ''}

## Implementation Timeline

| Phase | Duration | Description |
|-------|----------|-------------|
| Discovery | ${timeline.discovery} week${timeline.discovery > 1 ? 's' : ''} | Requirements gathering and solution definition |
| Design | ${timeline.design} week${timeline.design > 1 ? 's' : ''} | Solution architecture and technical design |
| Development | ${timeline.development} week${timeline.development > 1 ? 's' : ''} | Implementation of core features and AI components |
| Testing | ${timeline.testing} week${timeline.testing > 1 ? 's' : ''} | Quality assurance and user acceptance testing |
| Deployment | ${timeline.deployment} week${timeline.deployment > 1 ? 's' : ''} | Production deployment and system integration |

## Cost Estimate

Based on your specific requirements, we estimate the following costs:

### Development Costs
- Discovery & Design: $${costs.breakdown.development.discoveryDesign.min.toLocaleString()} - $${costs.breakdown.development.discoveryDesign.max.toLocaleString()}
- Implementation: $${costs.breakdown.development.implementation.min.toLocaleString()} - $${costs.breakdown.development.implementation.max.toLocaleString()}
- Testing & Deployment: $${costs.breakdown.development.testingDeployment.min.toLocaleString()} - $${costs.breakdown.development.testingDeployment.max.toLocaleString()}
- **Total Development:** $${costs.totalDevRange.min.toLocaleString()} - $${costs.totalDevRange.max.toLocaleString()}

### Ongoing Costs (Monthly)
- AWS Infrastructure: $${costs.breakdown.monthly.infrastructure.min.toLocaleString()} - $${costs.breakdown.monthly.infrastructure.max.toLocaleString()}
- Maintenance & Support: $${costs.breakdown.monthly.maintenance.min.toLocaleString()} - $${costs.breakdown.monthly.maintenance.max.toLocaleString()}
- Model Updates & Training: $${costs.breakdown.monthly.modelUpdates.min.toLocaleString()} - $${costs.breakdown.monthly.modelUpdates.max.toLocaleString()}
- **Total Monthly:** $${costs.totalMonthlyRange.min.toLocaleString()} - $${costs.totalMonthlyRange.max.toLocaleString()}

## Self Service Build Wizard

Our Self Service Build Wizard significantly reduces development costs and accelerates project delivery by:
- Automating AI infrastructure setup and configuration
- Providing pre-built AI components and model templates
- Streamlining integration with AWS AI/ML services
- Reducing manual coding requirements by up to 55%
- Enabling rapid prototyping and iteration of AI models

## Assumptions

This proposal is based on the following assumptions:
- Integration with up to ${complexity === 'basic' ? '1' : complexity === 'standard' ? '3' : '5'} existing systems
- Support for up to ${complexity === 'basic' ? '50' : complexity === 'standard' ? '100' : '500'} concurrent users
- Standard security requirements (no special compliance needs)
- Initial training data provided by client
- Use of our Self Service Build Wizard for accelerated development

## Next Steps

To proceed with this project, we recommend:
1. Schedule a detailed discovery workshop
2. Define success criteria and project scope
3. Sign statement of work
4. Begin design phase

Please contact us at info@relufox.ai to discuss this proposal further.`;
};

/**
 * Mock API call for document processing
 * @param {Object} requestData - Request data
 * @returns {Promise<Object>} Response data
 */
export const processDocument = async (requestData) => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 1500));
  
  // Check if this is a file upload or text message
  if (requestData.fileContent) {
    // Mock file processing response
    return {
      analysis: "I've analyzed your uploaded document. Based on the content, here's a proposal tailored to your requirements.",
      proposal: generateProposalTemplate("Requirements from uploaded document", requestData.context || {})
    };
  } else if (requestData.message) {
    // Check if we should use deterministic pricing
    const useDeterministicPricing = requestData.context?.useDeterministicPricing === true;
    
    // Get model parameters for project type detection
    const projectTypeParams = getModelParams('projectTypeDetection');
    const complexityParams = getModelParams('complexityClassification');
    
    // Log model parameters for debugging
    console.log('Using model parameters for project type detection:', projectTypeParams);
    console.log('Using model parameters for complexity classification:', complexityParams);
    
    let projectType, complexity, contextInfo;
    
    if (useDeterministicPricing) {
      // Use deterministic approach with regex pattern matching
      const extractedFeatures = extractProjectFeatures(requestData.message);
      
      // Merge extracted features with provided context
      contextInfo = {
        ...extractedFeatures,
        ...requestData.context,
        // Ensure projectType from context takes precedence if provided
        projectType: requestData.context?.projectType || extractedFeatures.projectType
      };
      
      projectType = contextInfo.projectType || 'default';
      complexity = determineComplexity(contextInfo);
      
      // Log deterministic results
      console.log('Deterministic project type:', projectType);
      console.log('Deterministic complexity:', complexity);
    } else {
      // Use the original approach with LLM-based detection
      projectType = requestData.context?.projectType;
      const lowerMessage = requestData.message.toLowerCase();
      
      if (!projectType) {
        if (lowerMessage.includes('agent') || lowerMessage.includes('autonomous') || 
            lowerMessage.includes('multi-step') || lowerMessage.includes('task planning') || 
            lowerMessage.includes('chat with my data') || lowerMessage.includes('assistant') || 
            (lowerMessage.includes('chatgpt') && lowerMessage.includes('like'))) {
          projectType = 'genai_agent';
        } else if (lowerMessage.includes('document') || lowerMessage.includes('contract') || 
            lowerMessage.includes('pdf') || lowerMessage.includes('extract') || 
            lowerMessage.includes('rag') || lowerMessage.includes('llm') || 
            lowerMessage.includes('nlp')) {
          projectType = 'document_ai';
        } else if (lowerMessage.includes('image') || lowerMessage.includes('vision') || 
                  lowerMessage.includes('object detection') || lowerMessage.includes('recognition')) {
          projectType = 'computer_vision';
        } else if (lowerMessage.includes('inventory') || lowerMessage.includes('stock') || lowerMessage.includes('order')) {
          projectType = 'inventory';
        } else if (lowerMessage.includes('chatbot') || lowerMessage.includes('chat')) {
          projectType = 'chatbot';
        } else if (lowerMessage.includes('data') || lowerMessage.includes('analytics')) {
          projectType = 'analytics';
        } else if (lowerMessage.includes('recommend') || lowerMessage.includes('suggestion')) {
          projectType = 'recommendation';
        } else {
          projectType = 'default';
        }
      }
      
      // Create context info from the original context
      contextInfo = {
        projectType,
        ...requestData.context
      };
      
      // Determine project complexity using the original method
      complexity = requestData.context?.complexity || 
                  classifyProjectComplexity({
                    projectType,
                    ...requestData.context
                  });
    }
    
    // Create a more detailed message by combining the original message with context
    let enhancedMessage = requestData.message;
    
    if (requestData.context) {
      // Only include relevant fields based on project type
      const { projectType, skuCount, productCount, warehouseCount, documentCount } = requestData.context;
      
      // Add SKU/product count only for inventory and recommendation projects
      if ((projectType === 'inventory' || projectType === 'recommendation') && (skuCount || productCount)) {
        if (skuCount) {
          enhancedMessage += `\nSKU Count: ${skuCount}`;
        } else if (productCount) {
          enhancedMessage += `\nProduct Count: ${productCount}`;
        }
      }
      
      // Add warehouse count only for inventory projects
      if (projectType === 'inventory' && warehouseCount) {
        if (typeof warehouseCount === 'number') {
          enhancedMessage += `\nWarehouses: ${warehouseCount}`;
        } else {
          enhancedMessage += '\nMultiple warehouse locations';
        }
      }
      
      // Add document count only for document_ai projects
      if (projectType === 'document_ai' && documentCount) {
        enhancedMessage += `\nDocument Count: ${documentCount}`;
      }
      
      // Integration type is relevant for all project types
      if (requestData.context.integrationType) {
        enhancedMessage += `\nIntegration: ${requestData.context.integrationType}`;
      }
    }
    
    let responseText = "Based on your requirements, I've prepared a detailed proposal";
    
    switch (projectType) {
      case 'genai_agent':
        // Build feature description
        let features = [];
        if (requestData.context?.needsMemory) features.push("persistent memory");
        if (requestData.context?.needsTools) features.push("tool integration");
        if (requestData.context?.needsOrchestration) features.push("workflow orchestration");
        
        const featuresText = features.length > 0 ? 
                           ` with ${features.join(", ")}` : "";
        
        responseText += ` for your ${complexity} complexity custom GenAI agent${featuresText}`;
        break;
      case 'document_ai':
        if (requestData.context?.specializedUseCase === 'legal_nlp') {
          responseText += ` for your ${complexity} complexity legal document processing system`;
        } else if (requestData.context?.specializedUseCase === 'medical_nlp') {
          responseText += ` for your ${complexity} complexity medical document analysis system`;
        } else if (requestData.context?.specializedUseCase === 'financial_nlp') {
          responseText += ` for your ${complexity} complexity financial document processing system`;
        } else {
          responseText += ` for your ${complexity} complexity document AI solution`;
        }
        break;
      case 'computer_vision':
        if (requestData.context?.specializedUseCase === 'medical_imaging') {
          responseText += ` for your ${complexity} complexity medical imaging analysis system`;
        } else {
          responseText += ` for your ${complexity} complexity computer vision solution`;
        }
        break;
      case 'inventory':
        responseText += ` for your ${complexity} complexity inventory management system`;
        break;
      case 'chatbot':
        responseText += ` for your ${complexity} complexity AI chatbot project`;
        break;
      case 'analytics':
        responseText += ` for your ${complexity} complexity data analytics solution`;
        break;
      case 'recommendation':
        responseText += ` for your ${complexity} complexity recommendation system`;
        break;
      default:
        responseText += ` for your ${complexity} complexity AI project`;
    }
    
    responseText += ". This proposal includes our Self Service Build Wizard to significantly reduce costs. You can download it as a PDF using the button below.";
    
    // Create a clean context for proposal generation to avoid interference between project types
    const baseProposalContext = {
      projectType,
      complexity,
      useSelfServiceWizard: requestData.context?.useSelfServiceWizard !== undefined ? requestData.context.useSelfServiceWizard : true,
      integrationType: requestData.context?.integrationType || 'none',
      dataSize: requestData.context?.dataSize || { size: 100, unit: 'GB' },
      hasIoTIntegration: requestData.context?.hasIoTIntegration || false,
      hasRealtimeRequirement: requestData.context?.hasRealtimeRequirement || false,
      hasComplianceRequirements: requestData.context?.hasComplianceRequirements || false,
      isEnterpriseScale: requestData.context?.isEnterpriseScale || false,
      integrationCount: requestData.context?.integrationCount || 1
    };
    
    // Add project-specific fields based on project type
    let proposalContext = { ...baseProposalContext };
    
    switch(projectType) {
      case 'inventory':
        proposalContext.skuCount = requestData.context?.skuCount || '';
        proposalContext.productCount = requestData.context?.productCount || '';
        proposalContext.warehouseCount = requestData.context?.warehouseCount || 1;
        break;
      case 'document_ai':
        proposalContext.documentCount = requestData.context?.documentCount || '';
        proposalContext.specializedUseCase = requestData.context?.specializedUseCase || 'none';
        break;
      case 'genai_agent':
        proposalContext.needsMemory = requestData.context?.needsMemory || false;
        proposalContext.needsTools = requestData.context?.needsTools || false;
        proposalContext.needsOrchestration = requestData.context?.needsOrchestration || false;
        break;
      case 'computer_vision':
      case 'recommendation':
        proposalContext.specializedUseCase = requestData.context?.specializedUseCase || 'none';
        if (projectType === 'recommendation') {
          proposalContext.skuCount = requestData.context?.skuCount || '';
          proposalContext.productCount = requestData.context?.productCount || '';
        }
        break;
      default:
        // No additional fields needed for other project types
        break;
    }
    
    // Mock text message response
    return {
      response: responseText,
      proposal: generateProposalTemplate(enhancedMessage, proposalContext)
    };
  }
  
  // Default response if no valid input
  return {
    response: "I couldn't process your request. Please provide project requirements or upload a document.",
    proposal: null
  };
};